import moment from 'moment'
import cloneDeep from 'lodash/cloneDeep'
import findKey from 'lodash/findKey'
import { addOwlError } from '@bike/fe-owl'
import {
  businessLineMap,
  businessLineTextMap,
  regExpMap,
  districtLevelMap,
  ResourceMap,
} from './consts'

import type { UploadFile } from 'antd/lib/upload/interface'

export const delay = (ms: number): Promise<undefined> => new Promise(res => setTimeout(res, ms))

export const isFalsy = (val: unknown): val is Falsy => !val

export const isNumber = (val: number | string): val is number =>
  regExpMap.posIntAndZero.test(val + '')

export const isPrimitive = (val: unknown): val is Primitive => {
  if (val === null || val === undefined) {
    return true
  }
  const typeDef = typeof val
  const primitiveNonNullishTypes = ['string', 'number', 'bigint', 'boolean', 'symbol']
  return primitiveNonNullishTypes.indexOf(typeDef) !== -1
}

export const getNamespace = (type: string) => /[A-Z]+/.exec(type)?.[0].toLocaleLowerCase() as string

// 时间格式相关方法
export const formatDate = (date: moment.Moment, format = 'YYYY-MM-DD'): string => {
  return date ? date.format(format) : ''
}

export const formatDateShowTime = (date: any): string => {
  return date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : ''
}

export const getDateTimestamp = (date: Date) => {
  return date ? moment(date).valueOf() : undefined
}

export const getMomentDate = (date: string) => {
  return date ? moment(date) : date
}

export const getDateTimestampWithoutMillisecond = (date: Date) => {
  const time = new Date(date)
  return Date.parse(time as any) // Date.parse() 不取毫秒
}

export const currentTime = () => {
  const time = moment().format('ll HH:mm:ss')
  const day = moment().format('dddd')
  return time.split(' ')[0] + ' (' + day + ') ' + ' ' + time.split(' ')[1]
}

export const getCurHour = () => +moment().format('HH')

export const getNextHour = () => +moment().format('HH') + 1

export const getTextWithDivider = (before: string, after?: string): string => {
  before = before || ''
  after = after || ''
  const divide = before && after ? '/' : ''
  return before + divide + after
}

export const getTextWithSuffix = (text = '', suffix = ''): string => {
  return text === null || text === undefined ? '' : text + suffix
}

export const validateOpsNumber = <T>(count: T): T | string => {
  return typeof count === 'number' ? count : '--'
}

export const validateOpsMeasure = <T, U>(count: T, measure: U): U | string => {
  return typeof count === 'number' ? measure : ''
}

export const getKeyByVal = <T extends Record<string, unknown>>(obj: T, value: ValueOf<T>) => {
  return Object.keys(obj).find(key => obj[key] === value)
}

export const getLabelByValue = (obj: any, value: any, key = 'label') => {
  return obj[findKey(obj, ['value', value]) as keyof typeof obj]?.[key]
}

export const getRecordId = (search: string) => {
  if (!search) {
    return ''
  }
  return /(?:recordId=)(\d+)/.exec(search)?.[1]
}

export const triggerResize = () => {
  setTimeout(function () {
    //手动触发窗口resize事件
    if (document.createEvent as any) {
      const event = new Event('resize')
      window.dispatchEvent(event)
    } else if (document.createEventObject as any) {
      window.fireEvent('onresize')
    }
  })
}

export const trimFormInput = (event: React.ChangeEvent<HTMLInputElement>) =>
  event.target.value ? event.target.value.trim() : ''

export const normFile = (e: any) => {
  let result = []
  if (Array.isArray(e)) {
    result = e
  } else {
    result = e ? e.fileList : []
  }
  return result.filter((item: any) => item.status !== undefined)
}

export const getFileList = (attachments: string[]): UploadFile[] => {
  if (!attachments || !attachments.length) {
    return []
  }
  return attachments.map((url, idx) => ({
    uid: '-' + (idx + 1),
    name: url,
    status: 'done',
    url: url,
  }))
}

export const getEleHeightByClassName = (className: string) => {
  const ele = document.getElementsByClassName(className)[0]
  if (!ele) {
    return
  }
  const range = document.createRange()
  range.selectNodeContents(ele)
  const rect = range.getBoundingClientRect()
  return rect.height
}

export const toLocaleString = (num: any): string => {
  return typeof num === 'number' ? (+num).toLocaleString() : '--'
}

export const getBizArrFromUserBiz = (biz?: BusinessType | null, needDouble = true) => {
  if (!biz) {
    return []
  } else if (biz === businessLineMap.double) {
    return needDouble
      ? Object.values(businessLineMap)
      : Object.values(businessLineMap).filter(item => item !== businessLineMap.double)
  } else {
    return [biz]
  }
}

export const getBizOptions = (businessType: BusinessType, needDoubleBiz: boolean) => {
  return getBizArrFromUserBiz(businessType, needDoubleBiz).map(item => ({
    label: businessLineTextMap[item as keyof typeof businessLineTextMap],
    value: item,
  }))
}

export const selectBizFromBizArr = (bizArr: BusinessType[]) =>
  bizArr.length > 1 ? businessLineMap.double : bizArr[0]

export const getFirstRoutePath = (routes: Routes): Route['path'] | undefined => {
  for (let i = 0; i < routes.length; i++) {
    const route = routes[i]
    if (route.routes) {
      return getFirstRoutePath(route.routes)
    } else {
      return route.path
    }
  }
}

export const filterRoutes = (routes: Routes): Routes => {
  const arr: Routes = []
  routes.forEach(route => {
    if (route.resource === undefined || route.resource) {
      arr.push(route)
      if (route.routes) {
        route.routes = filterRoutes(route.routes)
      }
    }
  })
  return arr
}

export const getLoginIframeUrl = () => {
  let hostName = ''
  let continueUrl = ''
  const Prefix = document.location.protocol.split(':')[0]

  if (location.href.indexOf('bike.meituan.com') > -1) {
    hostName = '//epassport.meituan.com/portal/login'
    continueUrl = encodeURIComponent(Prefix + '://bike.meituan.com/api/galaxy/toIndexPage')
  } else if (location.href.indexOf('api.bike.st.sankuai.com') > -1) {
    hostName = '//epassport.meituan.com/portal/login'
    continueUrl = encodeURIComponent(Prefix + '://api.bike.st.sankuai.com/api/galaxy/toIndexPage')
  } else {
    hostName = '//fepassport.sjst.test.sankuai.com/portal/login'
    // continueUrl = encodeURIComponent(
    //   Prefix + '://teste-sl-api.bike.test.sankuai.com/api/galaxy/toIndexPage'
    // )
    const swimLane = /:\/\/(.*)/.exec(window.location.origin)?.[1].split('-sl')[0]
    continueUrl = encodeURIComponent(
      Prefix + '://' + swimLane + '-sl-api.bike.test.sankuai.com/api/galaxy/toIndexPage'
    )
  }

  return (
    hostName +
    '?bg_source=12&feconfig=govregulation.platform.promotion&service=com.sankuai.govregulation.platform.promotion&continue=' +
    continueUrl
  )
}

export const getBoundsSwNe = (map: any) => {
  if (!map) {
    return {}
  }
  const bounds = map.getBounds()
  const sw = bounds.getSouthWest()
  const ne = bounds.getNorthEast()
  return { sw, ne }
}

export const getBoundsPolygon = (map: any) => {
  const { sw = {}, ne = {} } = getBoundsSwNe(map)
  const polygon =
    sw.lng +
    ',' +
    ne.lat +
    ';' +
    ne.lng +
    ',' +
    ne.lat +
    ';' +
    ne.lng +
    ',' +
    sw.lat +
    ';' +
    sw.lng +
    ',' +
    sw.lat
  return polygon
}

export const safeJsonParse = (jsonStr: string, dataType = {}) => {
  let data
  if (jsonStr) {
    try {
      data = JSON.parse(jsonStr)
    } catch (err) {
      data = dataType
    }
  } else {
    data = dataType
  }
  return data
}

export const changeLngLat = (arry = [], isLatLng = false): any => {
  const arr = []
  if (typeof arry[0] == 'number') {
    const latLng = isLatLng ? [arry[0], arry[1]] : [arry[1], arry[0]]
    return new TMap.LatLng(latLng[0], latLng[1])
  } else {
    for (let i = 0; i < arry.length; i++) {
      arr.push(changeLngLat(arry[i], isLatLng))
    }
  }
  return arr
}

/**
 * resourceIds 所有的子节点
 */
export const findAllParent = (tree: any, resourceIds: any[]) => {
  const plainedTree = plainTree(tree)
  const allParentIds: any[] = []
  resourceIds = resourceIds || []
  const walk = (plainedTree: any, resourceIds: any) => {
    // 找到上一层父节点
    const parentIds: any[] = []
    resourceIds.forEach((id: any) => {
      const parentId = plainedTree.find((node: any) => node.key === id)?.parentId
      if (parentId) {
        parentIds.push(parentId)
      }
    })
    allParentIds.push(...parentIds)
    if (parentIds?.length) {
      walk(plainedTree, [...(new Set(parentIds) as any)])
    }
  }
  walk(plainedTree, resourceIds)
  return [...(new Set(allParentIds) as any)]
}

export const plainTree = (rawArr: { children?: any[]; [name: string]: any }[] = []) => {
  let dataArr = cloneDeep(rawArr)
  const resultArr: any[] = []
  let i = 0
  while (i < dataArr.length) {
    const data: any = dataArr[i]
    if (data.children) {
      dataArr = dataArr.concat(data.children)
    }
    delete data.children
    resultArr.push(data)
    i++
  }
  return resultArr
}

export const getFindUserResoureceFn =
  (userResources: UserResources) => (code: UserResource['code'] | number) => {
    return userResources.find((item: UserResource) => +item?.code === +code) || null
  }

export const validateModuleResource = (resource?: { [name: string]: UserResource | null }) => {
  return resource && Object.values(resource).some(item => !!item)
}

export const validateAllResource = (resourceMap: ResourceMap) => {
  return Object.keys(resourceMap).some(resourceKey =>
    validateModuleResource(resourceMap[resourceKey as keyof typeof resourceMap])
  )
}

export const sliceTrendTitle = (title?: string) => {
  if (!title) {
    return ''
  }
  return title.length > 4 ? title.slice(0, 4) : title
}

export const transformArea = (data = [], cb?: (...arg: any[]) => void) => {
  const walk = (data: any, fatherChain?: any, fatherLevel?: any) => {
    data.forEach((item: any) => {
      item.title = item.adName || item.entityName
      item.key = item.adCode || item.id
      item.value = item.adCode || item.id
      item.chain = fatherChain ? fatherChain + ',' + item.adCode : item.adCode
      item.levelChain = fatherLevel ? fatherLevel + ',' + item.level : item.level
      item.disabled = [districtLevelMap.country, districtLevelMap.province].includes(+item.level)
      cb?.(item)
      if (item.children) {
        walk(item.children, item.chain, item.levelChain)
      }
    })
  }
  walk(data)
  return data
}

export const transformEntityTree = (entity: Entities, cb?: (e: Entity) => void) => {
  entity = entity || []
  const walk = (entity: Entities) => {
    entity.forEach(item => {
      if (item.children) {
        walk(item.children)
      }
      item.label = item.entityName || item.adName
      item.title = item.entityName || item.adName
      item.value = item.entityCode || item.adCode
      cb && typeof cb === 'function' && cb(item)
    })
  }
  walk(entity)
  return entity
}

export const findEntityTreeNode = (
  entityCode: Entity['entityCode'],
  entities: Entities
): Entity | undefined => {
  if (!entityCode || !entities?.length) {
    return
  }
  for (let i = 0; i < entities.length; i++) {
    const node = entities[i]
    if (node.entityCode === entityCode) {
      return node
    } else if (node.children) {
      const entity = findEntityTreeNode(entityCode, node.children)
      if (entity) {
        return entity
      }
    }
  }
}

export const findTreeNode = <T, K = string, V = unknown>(
  tree: T[],
  key: K,
  value: V,
  childName = 'children'
): T | undefined => {
  if (!tree?.length) {
    return
  }
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i]
    if (node[key as keyof T] === value) {
      return node
    } else if (node[childName as keyof T]) {
      const target = findTreeNode(node[childName as keyof T] as T[], key, value, childName)
      if (target) {
        return target
      }
    }
  }
}

export const tranformEntitiesToOptions = (entities: Entity[] = []): Options => {
  return entities.map(entity => ({
    ...entity,
    label: entity.entityName,
    value: entity.entityCode,
  })) as Options
}

export const toFixed = (num?: number | string, len = 3) => {
  return num ? Number(num).toFixed(len) : num
}

export const flatTreeList = (data: any) => {
  const dataList: any[] = []
  const generateList = (data: any) => {
    for (let i = 0; i < data.length; i++) {
      const node = data[i]
      const { key, title } = node
      dataList.push({ key, title })
      if (node.children) {
        generateList(node.children)
      }
    }
  }
  generateList(data)
  return dataList
}

export const getTreeParentKey = (key: any, tree: any): any => {
  let parentKey
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i]
    if (node.children) {
      if (node.children.some((item: any) => item.key === key)) {
        parentKey = node.key
      } else if (getTreeParentKey(key, node.children)) {
        parentKey = getTreeParentKey(key, node.children)
      }
    }
  }
  return parentKey
}

export const owlEventTrack = ({
  name,
  msg,
  level,
  category,
}: {
  name: string
  msg: string
  level: 'warn' | 'error'
  category: string
}) => {
  try {
    addOwlError({ name, msg, config: { level, category } })
  } catch (e) {
    // console.log('owl埋点错误', e)
  }
}

export const markName = (name: string): string => {
  let userName = ''
  if (!name) return name
  const len = name.length
  if (len == 2) {
    userName = name.substring(0, 1) + '*'
  } else if (len >= 3) {
    userName =
      name.substring(0, 1) +
      Array(len - 2)
        .fill('*')
        .join('') +
      name.substring(len - 1)
  }
  return userName
}

export const previewImage = (src: string) => {
  const preview = document.createElement('img')
  preview.src = src

  const overlay = document.createElement('div')
  overlay.style.position = 'fixed'
  overlay.style.top = '0'
  overlay.style.left = '0'
  overlay.style.width = '100%'
  overlay.style.height = '100%'
  overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
  overlay.appendChild(preview)
  // 在这里设置 img 元素的样式，使其占据整个屏幕
  preview.style.position = 'absolute'
  preview.style.maxWidth = '90%'
  preview.style.maxHeight = '80%'
  preview.style.top = '50%'
  preview.style.left = '50%'
  preview.style.transform = 'translate(-50%, -50%)'

  overlay.onclick = () => {
    document.body.removeChild(overlay)
  }
  document.body.appendChild(overlay)
}

// 判断是否为无效数值
export const isInValidData = (data: any) => {
  if (data === undefined || data === null || data === '') {
    return true
  }

  return false
}

export const range = (start: number, end: number) => {
  if (end < start) {
    console.warn('非法输入, end不允许小于start')
    return []
  }
  const result = []
  for (let i = start; i < end; i++) {
    result.push(i)
  }
  return result
}

// 加密姓名
export const encryptName = (name: string) => {
  if (isInValidData(name)) {
    return ''
  }
  return name.substring(0, 1) + '*'.repeat(name.length - 1)
}

export function lsSetItem(key: string, value: string) {
  localStorage?.setItem(key, value)
}

export function lsGetItem(key: string) {
  return localStorage?.getItem(key)
}

// 判断是否为微信内置浏览器
export function isWeChatBrowser(): boolean {
  return /micromessenger/i.test(window?.navigator?.userAgent)
}

// 判断是否为移动端
export function isMobile() {
  return /Mobi|Android|iPhone|iPad|iPod|Windows Phone|BlackBerry|HarmonyOS/i.test(
    window?.navigator?.userAgent
  )
}
